import React, {useEffect, useRef} from 'react'
import mapboxgl, {Map, Marker} from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {Button} from 'react-bootstrap'

interface SchoolMapProps {
  schoolLocation: {
    lng: number
    lat: number
  }
  onPositionChange?: (position: {latitude: number; longitude: number}) => void
  draggable?: boolean
  locateMe?: boolean
  interactive?: boolean
}

const SchoolMap: React.FC<SchoolMapProps> = ({
  schoolLocation,
  onPositionChange = () => {},
  draggable = false,
  locateMe = false,
  interactive = false,
}) => {
  const mapRef = useRef<Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const markerRef = useRef<Marker | null>(null)

  /**
   * Initializes the Mapbox map.
   */
  const initializeMap = (): Map => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || ''

    const map = new mapboxgl.Map({
      container: mapContainerRef.current as HTMLElement,
      center: [schoolLocation.lng, schoolLocation.lat],
      zoom: 16.5,
      maxZoom: 19,
      pitch: 60,
      attributionControl: false,
      interactive,
    })

    return map
  }

  /**
   * Adds a school marker to the map.
   * @param map - The Mapbox map instance.
   */
  const addSchoolMarker = (map: Map): Marker => {
    const schoolEl = document.createElement('i')
    schoolEl.classList.add('las', 'la-city', 'fs-2hx', 'text-warning', 'bg-white', 'p-2', 'rounded')

    const schoolMarker = new mapboxgl.Marker(schoolEl)
      .setLngLat([schoolLocation.lng, schoolLocation.lat])
      .setDraggable(draggable)
      .addTo(map)

    return schoolMarker
  }

  /**
   * Adds a drag end event listener to the marker.
   * @param marker - The Mapbox marker instance.
   */
  const addMarkerEvent = (marker: Marker) => {
    if (draggable && onPositionChange) {
      marker.on('dragend', () => {
        const newPosition = marker.getLngLat()
        onPositionChange({latitude: newPosition.lat, longitude: newPosition.lng})
      })
    }
  }

  useEffect(() => {
    mapRef.current = initializeMap()
    markerRef.current = addSchoolMarker(mapRef.current)

    addMarkerEvent(markerRef.current)

    return () => {
      mapRef.current?.remove()
    }
  }, [])

  useEffect(() => {
    markerRef.current?.setLngLat([schoolLocation.lng, schoolLocation.lat])
    mapRef.current?.setCenter([schoolLocation.lng, schoolLocation.lat])
  }, [schoolLocation])

  const goToCurrentLocation = () => {
    if (navigator.geolocation && mapRef.current) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const {latitude: currentLat, longitude: currentLng} = position.coords
          mapRef.current?.setCenter([currentLng, currentLat])
          markerRef.current?.setLngLat([currentLng, currentLat])
          onPositionChange?.({latitude: currentLat, longitude: currentLng})
        },
        (error) => console.error('Error getting current location:', error)
      )
    }
  }

  return (
    <div className="position-relative w-100 h-100">
      <div id="map-container" ref={mapContainerRef} className="w-100 h-100"></div>
      {locateMe && (
        <Button
          variant="light"
          className="position-absolute top-0 end-0 px-3 py-2 rounded-0 fw-bold bg-white shadow-sm"
          onClick={() => goToCurrentLocation()}
        >
          <i className="las la-crosshairs text-primary fs-2" />
          Drag to My Location
        </Button>
      )}
    </div>
  )
}

export default SchoolMap
